import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Checkbox — a value submitted with a form, or a row in a selection
 * (ADR-0088 D2). A setting that applies the moment it moves is a `Switch`.
 *
 * Built on Base UI (ADR-0088 D3). It replaces Admin's Radix checkbox and the
 * native checkboxes on Student's survey, coach filter and consent screens,
 * and keeps the Radix API Admin's forms were written against:
 * `checked={true | false | 'indeterminate'}` for the select-all mixed state,
 * and `onCheckedChange` called with the next value.
 *
 * **Two ways to label it, two elements.** Base UI renders the control beside
 * a hidden native input, and which element it is follows from the label:
 *
 * - **With an `id`**, a sibling `<label htmlFor={id}>` names it. The control
 *   is a `<button role="checkbox">` that carries the id, as Radix's was, so
 *   the label names one element and a click on it toggles the box.
 * - **Without an `id`**, a wrapping `<label>` or an `aria-label` names it.
 *   The control is Base UI's default `<span role="checkbox">`, so the hidden
 *   input is the label's only labelable element. A button there would make
 *   two, which HTML forbids and which happy-dom answers by bouncing the click
 *   between them, toggling the box a random number of times.
 *
 * So do not give an `id` to a checkbox inside its label. States come from
 * Base UI's `data-checked`, `data-indeterminate` and `data-disabled`.
 */
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { Icon } from './icon.js';
import { forwardRef } from 'react';
import { cn } from './cn.js';
import { controlFocus } from './field-shape.js';
export const Checkbox = forwardRef(({ className, checked, onCheckedChange, id, ...props }, ref) => (_jsx(CheckboxPrimitive.Root, { ref: ref, id: id, "data-slot": "checkbox", nativeButton: id !== undefined, render: id !== undefined ? _jsx("button", { type: "button" }) : undefined, checked: checked === undefined ? undefined : checked === true, indeterminate: checked === 'indeterminate', onCheckedChange: (next) => onCheckedChange?.(next), className: cn('peer inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-xs ' +
        'border border-border-strong bg-surface-card text-on-accent ' +
        `${controlFocus} ` +
        'data-disabled:cursor-not-allowed data-disabled:opacity-50 ' +
        'data-checked:border-accent data-checked:bg-accent ' +
        'data-indeterminate:border-accent data-indeterminate:bg-accent ' +
        // Disabled and checked, as a disabled primary button: the fill at half
        // strength and an ochre mark, where dimming turned the ink mark grey.
        'data-checked:data-disabled:opacity-100 data-checked:data-disabled:border-transparent ' +
        'data-checked:data-disabled:bg-accent/50 data-checked:data-disabled:text-accent-deep ' +
        'data-indeterminate:data-disabled:opacity-100 data-indeterminate:data-disabled:border-transparent ' +
        'data-indeterminate:data-disabled:bg-accent/50 data-indeterminate:data-disabled:text-accent-deep', className), ...props, children: _jsx(CheckboxPrimitive.Indicator, { className: "flex items-center justify-center [&_svg]:stroke-4", render: (indicatorProps, state) => (_jsx("span", { ...indicatorProps, children: state.indeterminate ? (_jsx(Icon, { name: "indeterminate", size: 12 })) : (_jsx(Icon, { name: "selected", size: 12 })) })) }) })));
Checkbox.displayName = 'Checkbox';
//# sourceMappingURL=checkbox.js.map